import HamburgerFullSvg from 'components/_common/Icons/SVGIcons/HambergurFullSvg';
import { placeParamsToURL } from 'helpers/CommonHelper';
import { t } from 'i18next';
import { Preloader } from 'modules/admin/components/_common/Preloader';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerMainRoutes } from 'routes/customer/CustomerRoutes';

// Every page here is a React.lazy route (see routes/customer/CustomerRoutes.tsx) - calling the
// same dynamic import ourselves reuses (or starts) that exact chunk fetch. Showing the loader
// until it resolves, then navigating, means the destination mounts with the chunk already in
// hand instead of flashing its own route-level Suspense fallback right after this one.
const menuItems: { labelKey: string; path: string; preload: () => Promise<unknown> }[] = [
  {
    labelKey: 'faq',
    path: placeParamsToURL(customerMainRoutes.faq.path),
    preload: () => import('modules/customer/pages/CustomerFAQ'),
  },
  {
    labelKey: 'sell_your_gun_to_gun_shops',
    path: placeParamsToURL(customerMainRoutes.sellFirearms.path),
    preload: () => import('modules/customer/pages/CustomerSellFirearms'),
  },
  {
    labelKey: 'ffl_locator',
    path: placeParamsToURL(customerMainRoutes.fflListing.path),
    preload: () => import('modules/customer/pages/CustomerFFLListing'),
  },
];

// Lets the browser's own behaviour (open in a new tab, save link, etc.) through untouched -
// the loader + preload only steps in for a plain left click that would navigate in place.
const isPlainLeftClick = (e: React.MouseEvent) =>
  e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

export default function AdditionalMenuSection() {
  const [isAdditionalMenuOpen, setIsAdditionalMenuOpen] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsAdditionalMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuItemClick = useCallback(
    (e: React.MouseEvent, item: (typeof menuItems)[number]) => {
      if (!isPlainLeftClick(e)) return;
      e.preventDefault();

      setIsAdditionalMenuOpen(false);
      if (isNavigating) return;

      setIsNavigating(true);
      item
        .preload()
        .catch(() => null)
        .finally(() => {
          navigate(item.path);
          setIsNavigating(false);
        });
    },
    [isNavigating, navigate]
  );

  return (
    <div className="additional-menu-wrapper" style={{ position: 'relative' }} ref={ref}>
      <Preloader loading={isNavigating} />
      <button
        className="middle-section__icon-btn"
        aria-label={t('menu')}
        onClick={() => setIsAdditionalMenuOpen((prev) => !prev)}
      >
        <HamburgerFullSvg />
      </button>
      {isAdditionalMenuOpen && (
        <div className="additional-menu-dropdown">
          <nav className="additional-menu-dropdown__menu">
            <a className="additional-menu-dropdown__item">{t('how_to_buy_gun_online')}</a>
            <a className="additional-menu-dropdown__item">{t('contact_us')}</a>
            {menuItems.map((item) => (
              <a
                key={item.path}
                href={item.path}
                className="additional-menu-dropdown__item"
                onClick={(e) => handleMenuItemClick(e, item)}
              >
                {t(item.labelKey)}
              </a>
            ))}
          </nav>
        </div>
      )}
    </div>
  );
}
